import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// 2025/2026 Federal tax brackets (approximate). Standard deductions.
const BRACKETS_2025 = {
  single: [[0,11925,10],[11925,48475,12],[48475,103350,22],[103350,197300,24],[197300,250525,32],[250525,626350,35],[626350,Infinity,37]],
  mfj: [[0,23850,10],[23850,96950,12],[96950,206700,22],[206700,394600,24],[394600,501050,32],[501050,751600,35],[751600,Infinity,37]],
  mfs: [[0,11925,10],[11925,48475,12],[48475,103350,22],[103350,197300,24],[197300,250525,32],[250525,375800,35],[375800,Infinity,37]],
  hoh: [[0,17000,10],[17000,64850,12],[64850,103350,22],[103350,197300,24],[197300,250525,32],[250525,626350,35],[626350,Infinity,37]],
};
const STD_DED_2025 = { single: 15000, mfj: 30000, mfs: 15000, hoh: 22500 };

function computeFederalTax(taxable, filing) {
  const brackets = BRACKETS_2025[filing] || BRACKETS_2025.single;
  let tax = 0;
  for (const [lo, hi, rate] of brackets) {
    if (taxable > lo) tax += (Math.min(taxable, hi) - lo) * rate/100;
    if (taxable <= hi) break;
  }
  return tax;
}

// Income Tax Calculator — mirrors Part 1 + Part 2 image (Deductions & Credits)
export default function IncomeTaxCalc() {
  const [filing, setFiling] = useState('single');
  const [youngDep, setYoungDep] = useState(0);
  const [otherDep, setOtherDep] = useState(0);
  const [taxYear, setTaxYear] = useState('2025');
  const [age, setAge] = useState(30);
  const [wages, setWages] = useState(80000);
  const [fedWH, setFedWH] = useState(9000);
  const [stateWH, setStateWH] = useState(0);
  const [localWH, setLocalWH] = useState(0);
  const [selfEmp, setSelfEmp] = useState('no');
  const [ssIncome, setSsIncome] = useState(0);
  const [interest, setInterest] = useState(0);
  const [ordDiv, setOrdDiv] = useState(0);
  const [qualDiv, setQualDiv] = useState(0);
  const [passive, setPassive] = useState(0);
  const [stCG, setStCG] = useState(0);
  const [ltCG, setLtCG] = useState(0);
  const [otherInc, setOtherInc] = useState(0);
  const [stateRate, setStateRate] = useState(0);
  // Deductions & Credits (part 2)
  const [tips, setTips] = useState(0);
  const [ot, setOt] = useState(0);
  const [carLoan, setCarLoan] = useState(0);
  const [ira, setIra] = useState(0);
  const [realEstateTax, setRealEstateTax] = useState(0);
  const [mortInt, setMortInt] = useState(0);
  const [charity, setCharity] = useState(0);
  const [studentLoan, setStudentLoan] = useState(0);
  const [childCare, setChildCare] = useState(0);
  const [college, setCollege] = useState(0);
  const [otherDeductibles, setOtherDeductibles] = useState(0);

  const c = useMemo(() => {
    const totalIncome = wages + ssIncome*0.85 + interest + ordDiv + passive + stCG + otherInc;
    const stdDed = STD_DED_2025[filing];
    const aboveLine = ira + Math.min(studentLoan, 2500);
    const itemized = realEstateTax + mortInt + charity + otherDeductibles;
    const useItemized = itemized > stdDed;
    const deduction = Math.max(stdDed, itemized) + aboveLine;
    const taxable = Math.max(0, totalIncome - deduction);
    const ordinaryTax = computeFederalTax(taxable - ltCG, filing);
    const ltCGTax = ltCG * 0.15; // simplified
    const totalFed = ordinaryTax + ltCGTax;
    // Credits
    const ctcCredit = youngDep * 2000;
    const odcCredit = otherDep * 500;
    const totalCredits = ctcCredit + odcCredit;
    const finalFed = Math.max(0, totalFed - totalCredits);
    const stateTax = (taxable * stateRate/100);
    const totalTax = finalFed + stateTax;
    const totalWH = fedWH + stateWH + localWH;
    const refund = totalWH - totalTax;
    return { totalIncome, deduction, useItemized, taxable, ordinaryTax, ltCGTax, totalFed, totalCredits, finalFed, stateTax, totalTax, totalWH, refund };
  }, [wages, ssIncome, interest, ordDiv, qualDiv, passive, stCG, ltCG, otherInc, filing, ira, studentLoan, realEstateTax, mortInt, charity, otherDeductibles, youngDep, otherDep, stateRate, fedWH, stateWH, localWH]);

  const $ = (v, on) => <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={v} onChange={e=>on(+e.target.value||0)} className="flex-1"/></div>;

  return (
    <CalcShell look="cnet" title="Income Tax Calculator" subtitle="Estimate your U.S. federal + state income tax refund or amount owed for tax year 2025 or 2026."
      about={<><p>This calculator estimates your U.S. federal income tax based on filing status, income sources, and standard/itemized deductions. Actual tax may differ based on specific credits and situations.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="font-semibold text-slate-900 mb-2">Basic Info</h3>
          <div className="field"><label>File Status</label>
            <select value={filing} onChange={e=>setFiling(e.target.value)}>
              <option value="single">Single</option><option value="mfj">Married Filing Jointly</option><option value="mfs">Married Filing Separately</option><option value="hoh">Head of Household</option>
            </select>
          </div>
          <div className="field"><label>No. of Young Dependents</label><input type="number" value={youngDep} onChange={e=>setYoungDep(+e.target.value||0)}/><p className="text-xs text-slate-500 mt-1">Age 0-16</p></div>
          <div className="field"><label>No. of Other Dependents</label><input type="number" value={otherDep} onChange={e=>setOtherDep(+e.target.value||0)}/><p className="text-xs text-slate-500 mt-1">Age 17 or older</p></div>
          <div className="field"><label>Tax Year</label>
            <div className="flex flex-col gap-1 mt-1 text-sm">
              <label className="flex items-center gap-2"><input type="radio" checked={taxYear==='2026'} onChange={()=>setTaxYear('2026')}/> 2026 (return filed in 2027)</label>
              <label className="flex items-center gap-2"><input type="radio" checked={taxYear==='2025'} onChange={()=>setTaxYear('2025')}/> 2025 (return filed in 2026)</label>
            </div>
          </div>
          <h3 className="font-semibold text-slate-900 mb-2 mt-4">Income</h3>
          <div className="field"><label>Age</label><input type="number" value={age} onChange={e=>setAge(+e.target.value||0)}/></div>
          <div className="field"><label>Wages, Tips, Other Compensation</label>{$(wages, setWages)}<p className="text-xs text-slate-500 mt-1">(W-2 box 1)</p></div>
          <div className="field"><label>Federal Income Tax Withheld</label>{$(fedWH, setFedWH)}<p className="text-xs text-slate-500 mt-1">(W-2 box 2)</p></div>
          <div className="field"><label>State Income Tax Withheld</label>{$(stateWH, setStateWH)}<p className="text-xs text-slate-500 mt-1">(W-2 box 17)</p></div>
          <div className="field"><label>Local Income Tax Withheld</label>{$(localWH, setLocalWH)}<p className="text-xs text-slate-500 mt-1">(W-2 box 19)</p></div>
          <div className="field"><label>Has Business or Self Employment Income?</label>
            <div className="flex gap-4 mt-1"><label><input type="radio" checked={selfEmp==='yes'} onChange={()=>setSelfEmp('yes')}/> yes</label><label><input type="radio" checked={selfEmp==='no'} onChange={()=>setSelfEmp('no')}/> no</label></div>
          </div>
          <div className="field"><label>Social Security Income</label>{$(ssIncome, setSsIncome)}<p className="text-xs text-slate-500 mt-1">SSA-1099, RRB-1099</p></div>
          <div className="field"><label>Interest Income</label>{$(interest, setInterest)}<p className="text-xs text-slate-500 mt-1">1099-INT</p></div>
          <div className="field"><label>Ordinary Dividends</label>{$(ordDiv, setOrdDiv)}</div>
          <div className="field"><label>Qualified Dividends</label>{$(qualDiv, setQualDiv)}<p className="text-xs text-slate-500 mt-1">1099-DIV</p></div>
          <div className="field"><label>Passive Incomes</label>{$(passive, setPassive)}<p className="text-xs text-slate-500 mt-1">e.g. rentals and real estate, royalties</p></div>
          <div className="field"><label>Short-term Capital Gains</label>{$(stCG, setStCG)}</div>
          <div className="field"><label>Long-term Capital Gains</label>{$(ltCG, setLtCG)}</div>
          <div className="field"><label>Other Income</label>{$(otherInc, setOtherInc)}<p className="text-xs text-slate-500 mt-1">e.g. unemployment pay (1099-G), retirement pay (1099-R)</p></div>
          <div className="field"><label>State+Local Tax Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={stateRate} onChange={e=>setStateRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>

          <h3 className="font-semibold text-slate-900 mb-2 mt-4">Deductions & Credits</h3>
          <div className="field"><label>Tips Income</label>{$(tips, setTips)}</div>
          <div className="field"><label>Overtime Income</label>{$(ot, setOt)}</div>
          <div className="field"><label>Car Loan Interest</label>{$(carLoan, setCarLoan)}<p className="text-xs text-slate-500 mt-1">Max $10,000 for qualified vehicle purchase</p></div>
          <div className="field"><label>IRA Contributions</label>{$(ira, setIra)}</div>
          <div className="field"><label>Real Estate Tax</label>{$(realEstateTax, setRealEstateTax)}</div>
          <div className="field"><label>Mortgage Interest</label>{$(mortInt, setMortInt)}</div>
          <div className="field"><label>Charitable Donations</label>{$(charity, setCharity)}</div>
          <div className="field"><label>Student Loan Interest</label>{$(studentLoan, setStudentLoan)}<p className="text-xs text-slate-500 mt-1">Max $2,500/Person</p></div>
          <div className="field"><label>Child & Dependent Care Expense</label>{$(childCare, setChildCare)}<p className="text-xs text-slate-500 mt-1">Max $3,000/Person, $6,000 total, up to age 13</p></div>
          <div className="field"><label>College Education Expense</label>{$(college, setCollege)}</div>
          <div className="field"><label>Other Deductibles</label>{$(otherDeductibles, setOtherDeductibles)}</div>
        </div>
        <div className="lg:sticky lg:top-4 self-start">
          <div className={`result-panel mb-4`} style={{background: c.refund > 0 ? '#dcfce7' : '#fee2e2'}}>
            <div className="result-title" style={{color: c.refund > 0 ? '#166534' : '#991b1b'}}>{c.refund > 0 ? 'Estimated Refund' : 'Amount Owed'}</div>
            <div className="result-value" style={{color: c.refund > 0 ? '#166534' : '#991b1b'}} data-testid="itax-refund">{fmt(Math.abs(c.refund))}</div>
          </div>
          <table className="data-table"><tbody>
            <tr><td>Total Income</td><td className="text-right">{fmt(c.totalIncome)}</td></tr>
            <tr><td>Standard/Itemized Deduction</td><td className="text-right">{fmt(c.deduction)}{c.useItemized ? ' (itemized)' : ''}</td></tr>
            <tr><td>Taxable Income</td><td className="text-right font-semibold">{fmt(c.taxable)}</td></tr>
            <tr><td>Ordinary Tax</td><td className="text-right">{fmt(c.ordinaryTax)}</td></tr>
            <tr><td>Long-Term CG Tax (15%)</td><td className="text-right">{fmt(c.ltCGTax)}</td></tr>
            <tr><td>Credits (CTC + ODC)</td><td className="text-right text-green-600">-{fmt(c.totalCredits)}</td></tr>
            <tr><td>Federal Tax</td><td className="text-right font-semibold">{fmt(c.finalFed)}</td></tr>
            <tr><td>State+Local Tax</td><td className="text-right">{fmt(c.stateTax)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Total Tax</td><td className="text-right">{fmt(c.totalTax)}</td></tr>
            <tr><td>Total Withheld</td><td className="text-right">{fmt(c.totalWH)}</td></tr>
          </tbody></table>
        </div>
      </div>
      <StepsPanel steps={[
        `Total income = wages + interest + dividends + gains = <b>${fmt(c.totalIncome)}</b>`,
        `Taxable income after deduction ${fmt(c.deduction)} = <b>${fmt(c.taxable)}</b>`,
        `Federal tax (progressive brackets ${filing.toUpperCase()}) = ${fmt(c.totalFed)} − credits ${fmt(c.totalCredits)} = <b>${fmt(c.finalFed)}</b>`,
        `Total tax including state/local = <b>${fmt(c.totalTax)}</b>`,
        `Withheld ${fmt(c.totalWH)} − tax owed ${fmt(c.totalTax)} = <b>${c.refund > 0 ? 'refund' : 'balance due'} ${fmt(Math.abs(c.refund))}</b>`,
      ]}/>
    </CalcShell>
  );
}
